/**
 * Global Currency Configuration
 * Supported currencies with symbols and formatting options
 */

export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
  nameAr?: string;
  decimalPlaces: number;
  symbolPosition: 'before' | 'after';
}

export const CURRENCIES: Record<string, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    nameAr: 'دولار أمريكي',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    nameAr: 'يورو',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    nameAr: 'جنيه إسترليني',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  SAR: {
    code: 'SAR',
    symbol: 'ر.س',
    name: 'Saudi Riyal',
    nameAr: 'ريال سعودي',
    decimalPlaces: 2,
    symbolPosition: 'after',
  },
  AED: {
    code: 'AED',
    symbol: 'د.إ',
    name: 'UAE Dirham',
    nameAr: 'درهم إماراتي',
    decimalPlaces: 2,
    symbolPosition: 'after',
  },
  KWD: {
    code: 'KWD',
    symbol: 'د.ك',
    name: 'Kuwaiti Dinar',
    nameAr: 'دينار كويتي',
    decimalPlaces: 3,
    symbolPosition: 'after',
  },
  BHD: {
    code: 'BHD',
    symbol: 'د.ب',
    name: 'Bahraini Dinar',
    nameAr: 'دينار بحريني',
    decimalPlaces: 3,
    symbolPosition: 'after',
  },
  QAR: {
    code: 'QAR',
    symbol: 'ر.ق',
    name: 'Qatari Riyal',
    nameAr: 'ريال قطري',
    decimalPlaces: 2,
    symbolPosition: 'after',
  },
  OMR: {
    code: 'OMR',
    symbol: 'ر.ع',
    name: 'Omani Rial',
    nameAr: 'ريال عماني',
    decimalPlaces: 3,
    symbolPosition: 'after',
  },
  EGP: {
    code: 'EGP',
    symbol: 'ج.م',
    name: 'Egyptian Pound',
    nameAr: 'جنيه مصري',
    decimalPlaces: 2,
    symbolPosition: 'after',
  },
  JOD: {
    code: 'JOD',
    symbol: 'د.أ',
    name: 'Jordanian Dinar',
    nameAr: 'دينار أردني',
    decimalPlaces: 3,
    symbolPosition: 'after',
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    nameAr: 'روبية هندية',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  PKR: {
    code: 'PKR',
    symbol: '₨',
    name: 'Pakistani Rupee',
    nameAr: 'روبية باكستانية',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    nameAr: 'دولار كندي',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    nameAr: 'دولار أسترالي',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    nameAr: 'ين ياباني',
    decimalPlaces: 0,
    symbolPosition: 'before',
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'Chinese Yuan',
    nameAr: 'يوان صيني',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  TRY: {
    code: 'TRY',
    symbol: '₺',
    name: 'Turkish Lira',
    nameAr: 'ليرة تركية',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  MYR: {
    code: 'MYR',
    symbol: 'RM',
    name: 'Malaysian Ringgit',
    nameAr: 'رينغيت ماليزي',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    name: 'Singapore Dollar',
    nameAr: 'دولار سنغافوري',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
};

export const CURRENCY_CODES = Object.keys(CURRENCIES);

export const DEFAULT_CURRENCY = 'SAR';

/**
 * Get currency config by code
 */
export function getCurrencyConfig(code: string): CurrencyConfig | undefined {
  return CURRENCIES[code.toUpperCase()];
}

/**
 * Format amount with currency
 */
export function formatCurrency(
  amount: number,
  currencyCode: string = DEFAULT_CURRENCY,
  locale?: string
): string {
  const currency = getCurrencyConfig(currencyCode);
  if (!currency) {
    return `${amount} ${currencyCode}`;
  }

  try {
    return new Intl.NumberFormat(locale || 'en', {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 0,
      maximumFractionDigits: currency.decimalPlaces,
    }).format(amount);
  } catch {
    // Fallback for unsupported currencies
    const formatted = amount.toFixed(currency.decimalPlaces);
    return currency.symbolPosition === 'before'
      ? `${currency.symbol}${formatted}`
      : `${formatted} ${currency.symbol}`;
  }
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(code: string): string {
  return getCurrencyConfig(code)?.symbol || code;
}
